/**
 * Onglet Anniversaires de l'interface d'administration
 * Permet de tester et surveiller le système d'annonces d'anniversaires
 * @module admin/tabs/BirthdaysTab
 */

import React, { useEffect } from 'react';
import { useBirthdays } from '../hooks/useBirthdays';
import AdminResult from '../components/AdminResult';

const BirthdaysTab = () => {
  const {
    birthdayStatus,
    birthdayHistory,
    birthdayLoading,
    birthdayResult,
    birthdayError,
    fetchBirthdayStatus,
    fetchBirthdayHistory,
    handleTestAnnounce,
    handleCheckBirthdays,
  } = useBirthdays();

  useEffect(() => {
    fetchBirthdayStatus();
    fetchBirthdayHistory();
  }, [fetchBirthdayStatus, fetchBirthdayHistory]);

  const renderStatus = () => {
    if (!birthdayStatus) return null;

    return (
      <div className="admin-birthday-status">
        <h3>Statut actuel</h3>
        <div className="admin-birthday-status-grid">
          <div className="admin-birthday-status-item">
            <span className="admin-birthday-status-label">Annoncer configuré:</span>
            <span className={`admin-birthday-status-value ${birthdayStatus.announcerConfigured ? 'success' : 'error'}`}>
              {birthdayStatus.announcerConfigured ? '✅ Oui' : '❌ Non'}
            </span>
          </div>
          <div className="admin-birthday-status-item">
            <span className="admin-birthday-status-label">Annoncer prêt:</span>
            <span className={`admin-birthday-status-value ${birthdayStatus.announcerReady ? 'success' : 'warning'}`}>
              {birthdayStatus.announcerReady ? '✅ Oui' : '⚠️ Non (bot pas connecté au chat)'}
            </span>
          </div>
          <div className="admin-birthday-status-item">
            <span className="admin-birthday-status-label">Anniversaires aujourd'hui:</span>
            <span className="admin-birthday-status-value">
              {birthdayStatus.todayBirthdaysCount > 0 
                ? `🎂 ${birthdayStatus.todayBirthdaysCount} (${birthdayStatus.todayBirthdayUsernames.join(', ')})`
                : '📅 Aucun'}
            </span>
          </div>
          <div className="admin-birthday-status-item">
            <span className="admin-birthday-status-label">Déjà annoncé aujourd'hui:</span>
            <span className={`admin-birthday-status-value ${birthdayStatus.alreadyAnnouncedToday ? 'success' : 'neutral'}`}>
              {birthdayStatus.alreadyAnnouncedToday ? '✅ Oui' : '❌ Non'}
            </span>
          </div>
          <div className="admin-birthday-status-item">
            <span className="admin-birthday-status-label">Peut annoncer:</span>
            <span className={`admin-birthday-status-value ${birthdayStatus.canAnnounce ? 'success' : 'neutral'}`}>
              {birthdayStatus.canAnnounce ? '✅ Oui' : '❌ Non'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderHistory = () => {
    if (!birthdayHistory || birthdayHistory.length === 0) {
      return (
        <div className="admin-birthday-history">
          <h3>📜 Historique des annonces</h3>
          <p className="admin-placeholder">Aucune annonce enregistrée</p>
        </div>
      );
    }

    return (
      <div className="admin-birthday-history">
        <h3>📜 Historique des annonces ({birthdayHistory.length})</h3>
        <div className="admin-birthday-history-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Nombre</th>
                <th>Utilisateurs</th>
                <th>Heure</th>
              </tr>
            </thead>
            <tbody>
              {birthdayHistory.map((announcement) => (
                <tr key={announcement.id}>
                  <td>{announcement.announcementDate}</td>
                  <td>{announcement.birthdayCount}</td>
                  <td>{announcement.birthdayUsernames || '-'}</td>
                  <td>{new Date(announcement.createdAt).toLocaleTimeString('fr-FR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderResult = () => {
    if (!birthdayResult) return null;

    const { success, message, data } = birthdayResult;

    return (
      <div className={`admin-result ${success ? 'admin-result-success' : 'admin-result-error'}`}>
        <h3>{success ? '✅ Succès' : '❌ Erreur'}</h3>
        <p><strong>Message:</strong> {message}</p>
        {data && (
          <div className="admin-birthday-result-details">
            <p><strong>Anniversaires trouvés:</strong> {data.birthdaysFound}</p>
            {data.birthdayUsernames && data.birthdayUsernames.length > 0 && (
              <p><strong>Utilisateurs:</strong> {data.birthdayUsernames.join(', ')}</p>
            )}
            {data.messageContent && (
              <p><strong>Message envoyé:</strong> {data.messageContent}</p>
            )}
            <p><strong>Annoncer prêt:</strong> {data.announcerReady ? 'Oui' : 'Non'}</p>
            <p><strong>Envoi tenté:</strong> {data.sendAttempted ? 'Oui' : 'Non'}</p>
            <p><strong>Envoi réussi:</strong> {data.sendSuccess ? 'Oui' : 'Non'}</p>
            {data.error && <p><strong>Erreur:</strong> {data.error}</p>}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="admin-section">
      <div className="admin-section-header">
        <h2>🎂 Anniversaires</h2>
        <div className="admin-button-group">
          <button
            onClick={handleCheckBirthdays}
            disabled={birthdayLoading}
            className="admin-button admin-button-secondary"
            title="Vérifie et envoie l'annonce si nécessaire (avec anti-doublon)"
          >
            {birthdayLoading ? '🔄 Vérification...' : '🔍 Vérifier les anniversaires'}
          </button>
          <button
            onClick={handleTestAnnounce}
            disabled={birthdayLoading}
            className="admin-button admin-button-primary"
            title="Force l'envoi de l'annonce (ignore l'anti-doublon)"
          >
            {birthdayLoading ? '🔄 Envoi...' : '🧪 Tester l\'annonce (aujourd\'hui)'}
          </button>
        </div>
      </div>

      <p className="admin-section-description">
        Testez et surveillez le système d'annonces d'anniversaires. Le bouton "Tester" 
        envoie réellement le message dans le chat Twitch (si le bot est connecté) et 
        ignore l'anti-doublon.
      </p>

      {birthdayError && (
        <AdminResult isSuccess={false} title="Erreur" message={birthdayError} />
      )}

      {renderResult()}
      {renderStatus()}
      {renderHistory()}

      {birthdayLoading && !birthdayStatus && (
        <div className="admin-placeholder">
          <p>Chargement des données...</p>
        </div>
      )}
    </section>
  );
};

export default BirthdaysTab;
